import { Controller, type Control, type UseFormSetValue, type UseFormWatch } from "react-hook-form";
import { Autocomplete, TextField } from "@mui/material";
import { FormLabel } from "../../../../../components-new";
import { fstTemplateLabel, fsdAcLabel } from "./connectDataBankLabels";
import {
    DATA_BANK_VIEW_CASTE_SUMMARY,
    DATA_BANK_VIEW_CONSTITUENCY_MAP,
    DATA_BANK_VIEW_PAST_ELECTION,
    DATA_BANK_VIEW_OPTIONS,
    type DataBankViewType,
} from "./dataBankViewTypes";
import type {
    FlexiStoreData_Abridged,
    FlexiStoreTemplate_Abridged,
} from "../../../../../stores/fs/fst";

export type ConnectDataBankFormVals = {
    name: string;
    fst: FlexiStoreTemplate_Abridged | null;
    fsd: FlexiStoreData_Abridged | null;
    viewType: DataBankViewType | "";
    election: string;
    mapKey: string;
};

type Props = {
    control: Control<ConnectDataBankFormVals>;
    setValue: UseFormSetValue<ConnectDataBankFormVals>;
    watch: UseFormWatch<ConnectDataBankFormVals>;
    fstList: FlexiStoreTemplate_Abridged[];
    fsdList: FlexiStoreData_Abridged[];
    elections: string[];
    mapKeys: string[];
    fstLoading: boolean;
    fsdLoading: boolean;
    elLoading: boolean;
    mapLoading: boolean;
    isEdit: boolean;
};

function resetViewFields(setValue: UseFormSetValue<ConnectDataBankFormVals>) {
    setValue("viewType", "");
    setValue("election", "");
    setValue("mapKey", "");
}

export function ConnectDataBankPickers({
    control,
    setValue,
    watch,
    fstList,
    fsdList,
    elections,
    mapKeys,
    fstLoading,
    fsdLoading,
    elLoading,
    mapLoading,
    isEdit,
}: Props) {
    const fst = watch("fst");
    const fsd = watch("fsd");
    const viewType = watch("viewType");

    return (
        <>
            <div className="mb-3">
                <FormLabel className="mb-2 block">Data bank</FormLabel>
                <Controller
                    name="fst"
                    control={control}
                    render={({ field }) => (
                        <Autocomplete
                            options={fstList}
                            loading={fstLoading}
                            getOptionLabel={(o) => (o ? fstTemplateLabel(o) : "")}
                            value={field.value}
                            onChange={(_e, v) => {
                                field.onChange(v);
                                setValue("fsd", null);
                                resetViewFields(setValue);
                            }}
                            disabled={isEdit}
                            renderInput={(p) => (
                                <TextField {...p} size="small" placeholder="Select data bank" />
                            )}
                        />
                    )}
                />
            </div>
            <div className="mb-3">
                <FormLabel className="mb-2 block">Assembly (AC)</FormLabel>
                <Controller
                    name="fsd"
                    control={control}
                    render={({ field }) => (
                        <Autocomplete
                            options={fsdList}
                            loading={fsdLoading}
                            getOptionLabel={(o) => (o ? fsdAcLabel(o) : "")}
                            value={field.value}
                            onChange={(_e, v) => {
                                field.onChange(v);
                                resetViewFields(setValue);
                            }}
                            disabled={isEdit || !fst}
                            renderInput={(p) => <TextField {...p} size="small" placeholder="Select AC" />}
                        />
                    )}
                />
            </div>
            <div className="mb-3">
                <FormLabel className="mb-2 block">Content</FormLabel>
                <Controller
                    name="viewType"
                    control={control}
                    render={({ field }) => (
                        <Autocomplete
                            options={DATA_BANK_VIEW_OPTIONS}
                            getOptionLabel={(o) => o.label}
                            isOptionEqualToValue={(a, b) => a.value === b.value}
                            value={DATA_BANK_VIEW_OPTIONS.find((o) => o.value === field.value) ?? null}
                            onChange={(_e, v) => {
                                field.onChange(v?.value ?? "");
                                setValue("election", "");
                                setValue("mapKey", "");
                            }}
                            disabled={isEdit || !fsd}
                            renderInput={(p) => (
                                <TextField {...p} size="small" placeholder="Select content type" />
                            )}
                        />
                    )}
                />
            </div>
            {viewType === DATA_BANK_VIEW_PAST_ELECTION && (
                <div className="mb-3">
                    <FormLabel className="mb-2 block">Year</FormLabel>
                    <Controller
                        name="election"
                        control={control}
                        render={({ field }) => (
                            <Autocomplete
                                options={elections}
                                loading={elLoading}
                                value={field.value || null}
                                onChange={(_e, v) => field.onChange(v ?? "")}
                                disabled={isEdit || !fsd}
                                renderInput={(p) => (
                                    <TextField {...p} size="small" placeholder="Select year" />
                                )}
                            />
                        )}
                    />
                    {!elLoading && elections.length === 0 && (
                        <p className="mt-2 text-xs text-amber-700">No election years available for this AC.</p>
                    )}
                </div>
            )}
            {viewType === DATA_BANK_VIEW_CONSTITUENCY_MAP && (
                <div className="mb-3">
                    <FormLabel className="mb-2 block">Map type</FormLabel>
                    <Controller
                        name="mapKey"
                        control={control}
                        render={({ field }) => (
                            <Autocomplete
                                options={mapKeys}
                                loading={mapLoading}
                                value={field.value || null}
                                onChange={(_e, v) => field.onChange(v ?? "")}
                                disabled={isEdit || !fsd}
                                renderInput={(p) => (
                                    <TextField {...p} size="small" placeholder="Select map" />
                                )}
                            />
                        )}
                    />
                    {!mapLoading && mapKeys.length === 0 && (
                        <p className="mt-2 text-xs text-amber-700">No constituency maps available for this AC.</p>
                    )}
                </div>
            )}
            {viewType === DATA_BANK_VIEW_CASTE_SUMMARY && (
                <p className="mb-3 text-xs text-gray-500">Overall caste summary will be shown in analytics.</p>
            )}
        </>
    );
}
